// =========================================================================
// SightLine Networks — Production Website
// React + Tailwind CSS | Single-file implementation
//
// SEO: Each page has unique H1, meta title/description, canonical,
//      OG/Twitter tags, and JSON-LD schema managed by SeoManager.
//
// Google Ads: CTA buttons carry data-cta and data-page attributes.
//             trackCta() pushes to dataLayer (GTM) and gtag (GA4).
//             Form submissions fire a conversion-friendly event.
//
// Integration notes:
//   - Replace setPage() routing with React Router <Link> for production
//   - Connect ContactForm to EmailJS / Supabase / your backend
//   - Add GTM container snippet in public/index.html <head>
//   - Add GA4 config snippet or load via GTM
//   - Add Google Ads conversion linker + conversion tags in GTM
// =========================================================================

import { useEffect, useMemo, useState } from "react";
import {
  ChevronRight,
  Shield,
  Globe,
  Server,
  Monitor,
  Menu,
  X,
  CheckCircle2,
  MapPin,
  Headphones,
  Clock3,
  BadgeCheck,
  LineChart,
  Lock,
  Phone,
  Mail,
  Wifi,
  Camera,
  Cloud,
  ArrowRight,
  Check,
  Zap,
  HardDrive,
  Wrench,
  Building2,
} from "lucide-react";

// =========================================================================
// TYPES
// =========================================================================

type PageKey =
  | "home"
  | "services"
  | "hosting"
  | "solutions"
  | "areas"
  | "about"
  | "contact"
  | "managed-it"
  | "network-installation"
  | "security-cameras"
  | "server-cloud"
  | "website-development"
  | "it-consulting";

type HeaderProps = {
  setPage: (page: PageKey) => void;
  page: PageKey;
  mobileOpen: boolean;
  setMobileOpen: (open: boolean) => void;
};

type SeoConfig = {
  title: string;
  description: string;
  canonical: string;
  ogTitle: string;
  ogDescription: string;
  keywords?: string;
};

// =========================================================================
// CONSTANTS
// =========================================================================

const BASE_URL = "https://sightlinenetworks.com";
const PHONE = "(727) 283-6452";
const PHONE_HREF = "tel:17272836452";
const EMAIL = "info@sightlinenetworks.com";

const NAV_ITEMS: Array<{ key: PageKey; label: string }> = [
  { key: "services", label: "Services" },
  { key: "hosting", label: "Hosting" },
  { key: "solutions", label: "Solutions" },
  { key: "areas", label: "Areas Served" },
  { key: "about", label: "About" },
];

// Home page FAQ — visible content in HomePage MUST stay in sync with the
// FAQPage JSON-LD in public/index.html for Google rich-result eligibility.
const HOME_FAQS: Array<{ q: string; a: string }> = [
  {
    q: "Are you a certified Ubiquiti UniFi installer in Tampa Bay?",
    a: "Yes. SightLine Networks designs and installs Ubiquiti UniFi systems for Tampa Bay businesses, including UniFi Network (switches, access points, gateways), UniFi Protect security cameras, and UniFi Access door control. We handle planning, deployment, configuration, and ongoing support.",
  },
  {
    q: "Do you install Cisco networking and Cisco VOIP for businesses?",
    a: "Yes. We install Cisco and Cisco Meraki networking equipment — switches, routers, firewalls, and wireless — plus Cisco VOIP phone systems for offices that need enterprise reliability and call routing.",
  },
  {
    q: "Can you set up a Synology NAS for local backup?",
    a: "Absolutely. Synology NAS is one of our recommended storage and backup platforms. We size the unit, configure Synology DSM, set up RAID and snapshots, integrate with cloud backup providers, and document the recovery procedure for your team.",
  },
  {
    q: "Which IP camera brands do you install?",
    a: "We install Ubiquiti UniFi Protect, Hikvision, Axis Communications, Dahua, Amcrest, and Reolink. We pick the brand based on your site, budget, and integration needs (NVR vs. cloud, indoor vs. outdoor, license-plate or analytics requirements).",
  },
  {
    q: "Which VOIP phone systems do you support?",
    a: "We deploy and support business VOIP phones from Yealink, Poly (Polycom), and Grandstream, plus Cisco VOIP for enterprise environments. We can integrate with cloud PBX providers or set up a local PBX.",
  },
  {
    q: "Do you only work in Tampa Bay?",
    a: "We're based in Clearwater, FL and primarily serve the Tampa Bay area — Tampa, Clearwater, St. Petersburg, Largo, Pinellas Park, and Brandon. Larger projects outside the immediate area can be discussed on a case-by-case basis.",
  },
  {
    q: "How much does a UniFi or security camera installation cost?",
    a: "Cost depends on site size, number of access points or cameras, cabling required, and whether existing infrastructure can be reused. Most projects fall between a few thousand dollars for small offices and tens of thousands for multi-building deployments. We provide free, itemized quotes after a brief site discussion.",
  },
];

const SEO_CONFIG: Record<PageKey, SeoConfig> = {
  home: {
    title:
      "Ubiquiti UniFi, Cisco & Synology Installer in Tampa Bay | Managed IT, Networks & Security Cameras | SightLine Networks",
    description:
      "Tampa Bay installer of Ubiquiti UniFi (UniFi Network, UniFi Protect, UniFi Access), Cisco networking and VOIP, Synology NAS, and security cameras from Hikvision, Axis, and Dahua. Managed IT, business hosting, and website development. Free quote.",
    canonical: `${BASE_URL}/`,
    ogTitle:
      "SightLine Networks | Ubiquiti UniFi, Cisco & Synology Specialists in Tampa Bay",
    ogDescription:
      "Certified installers of Ubiquiti UniFi Network & Protect, Cisco networking and VOIP, Synology backup, and IP security cameras for Tampa Bay businesses.",
    keywords:
      "Ubiquiti, UniFi, UniFi Protect, UniFi Network, UniFi Access, Ubiquiti installer Tampa, Cisco networking, Cisco VOIP, Synology NAS, Synology backup, security cameras Tampa Bay, IP cameras, Hikvision, Axis, Dahua, Yealink, Poly VOIP, Grandstream, business IT Tampa, managed IT Tampa Bay, network installation",
  },
  services: {
    title:
      "Business IT Services in Tampa Bay | Managed IT, Networks, Security & Web | SightLine Networks",
    description:
      "Explore IT services from SightLine Networks: managed IT support, network installation, security camera systems, server infrastructure, website development, and business web hosting across Tampa Bay.",
    canonical: `${BASE_URL}/services`,
    ogTitle: "Business IT Services | SightLine Networks",
    ogDescription:
      "Managed IT, network installation, security cameras, server support, website development, and hosting for Tampa Bay businesses.",
  },
  hosting: {
    title:
      "Business Web Hosting Plans | Managed Hosting with SSL & Backups | SightLine Networks",
    description:
      "Reliable managed web hosting for business websites with free SSL, daily backups, uptime monitoring, and expert support. Plans start at $29/mo. Hosted and supported locally in Tampa Bay.",
    canonical: `${BASE_URL}/hosting`,
    ogTitle: "Managed Business Web Hosting | SightLine Networks",
    ogDescription:
      "Fast, secure hosting plans for business websites with SSL, daily backups, monitoring, and Tampa Bay-based support.",
  },
  solutions: {
    title:
      "Business Technology Solutions | Office IT, Wi-Fi, Security & Websites | SightLine Networks",
    description:
      "Find the right technology solution for your business. From office IT setup and Wi-Fi optimization to security cameras, web hosting, and ongoing support across Tampa Bay.",
    canonical: `${BASE_URL}/solutions`,
    ogTitle: "Business Technology Solutions | SightLine Networks",
    ogDescription:
      "Problem-focused technology solutions for Tampa Bay businesses: office setup, networking, security, hosting, and websites.",
  },
  areas: {
    title:
      "IT Services in Tampa, Clearwater, Largo, St. Petersburg & Tampa Bay | SightLine Networks",
    description:
      "SightLine Networks provides managed IT, web hosting, network installation, security cameras, and website development to businesses in Tampa, Clearwater, Largo, St. Petersburg, and across Tampa Bay.",
    canonical: `${BASE_URL}/areas-served`,
    ogTitle: "Tampa Bay IT Services Coverage | SightLine Networks",
    ogDescription:
      "Local IT services, hosting, networking, security, and web solutions across the Tampa Bay area including Tampa, Clearwater, and St. Petersburg.",
  },
  about: {
    title:
      "About SightLine Networks | Your Tampa Bay IT & Hosting Partner",
    description:
      "SightLine Networks is a Tampa Bay technology company helping businesses with managed IT, web hosting, network infrastructure, security cameras, and website development since day one.",
    canonical: `${BASE_URL}/about`,
    ogTitle: "About SightLine Networks",
    ogDescription:
      "A trusted technology partner for Tampa Bay businesses needing IT support, hosting, networking, security, and web solutions.",
  },
  contact: {
    title:
      "Contact SightLine Networks | Get a Free IT Services Quote",
    description:
      "Contact SightLine Networks for a free quote on managed IT, web hosting, network installation, security cameras, or website development. Serving Tampa Bay businesses. Call (727) 283-6452.",
    canonical: `${BASE_URL}/contact`,
    ogTitle: "Contact SightLine Networks | Get a Quote",
    ogDescription:
      "Request a free quote for managed IT, hosting, websites, networking, or security camera projects in Tampa Bay.",
  },
  "managed-it": {
    title: "Managed IT Services in Tampa Bay | Ubiquiti, Cisco & Synology Support | SightLine Networks",
    description: "Proactive managed IT for Tampa Bay businesses. 24/7 monitoring, helpdesk, patch management, cybersecurity, and ongoing support for Ubiquiti UniFi, Cisco, and Synology environments. Free IT assessment.",
    canonical: `${BASE_URL}/services/managed-it`,
    ogTitle: "Managed IT Services | SightLine Networks",
    ogDescription: "24/7 managed IT support for Ubiquiti, Cisco, and Synology environments in Tampa Bay.",
    keywords: "managed IT Tampa Bay, IT support Tampa, Ubiquiti UniFi support, Cisco support, Synology support, helpdesk Tampa, business IT monitoring, cybersecurity Tampa, patch management",
  },
  "network-installation": {
    title: "Ubiquiti UniFi & Cisco Network Installation in Tampa Bay | Wi-Fi, Switches & Cabling | SightLine Networks",
    description: "Professional Ubiquiti UniFi and Cisco network installation for Tampa Bay businesses. UniFi Network design, UniFi access points, Cisco/Meraki switches, structured cabling, firewalls, and enterprise Wi-Fi deployment.",
    canonical: `${BASE_URL}/services/network-installation`,
    ogTitle: "Ubiquiti UniFi & Cisco Network Installation | SightLine Networks",
    ogDescription: "Certified UniFi and Cisco installers in Tampa Bay. Enterprise Wi-Fi, switches, firewalls, and structured cabling for businesses.",
    keywords: "Ubiquiti UniFi installer Tampa, UniFi Network, UniFi access points, Cisco networking, Meraki, network installation Tampa Bay, business Wi-Fi installation, structured cabling Tampa, enterprise Wi-Fi, network switch installation, firewall installation",
  },
  "security-cameras": {
    title: "UniFi Protect, Hikvision & Axis Security Camera Installation in Tampa Bay | SightLine Networks",
    description: "Commercial IP security cameras for Tampa Bay businesses. Certified installers of Ubiquiti UniFi Protect, Hikvision, Axis, and Dahua cameras with 4K video, remote viewing, NVR/cloud recording, and access control.",
    canonical: `${BASE_URL}/services/security-cameras`,
    ogTitle: "UniFi Protect & IP Camera Installation | SightLine Networks",
    ogDescription: "Professional IP security camera systems from Ubiquiti UniFi Protect, Hikvision, Axis, and Dahua for Tampa Bay businesses.",
    keywords: "UniFi Protect installer, Ubiquiti security cameras, IP camera installation Tampa, Hikvision installer, Axis Communications cameras, Dahua, Amcrest, Reolink, commercial security cameras Tampa Bay, business surveillance, 4K IP cameras, NVR installation, access control Tampa",
  },
  "server-cloud": {
    title: "Synology NAS, Server & Cloud Infrastructure in Tampa Bay | Local Backup & Migration | SightLine Networks",
    description: "Synology NAS installation, server deployment, cloud migration, and disaster recovery for Tampa Bay businesses. Local backup, hybrid infrastructure, virtualization, and Synology DSM support.",
    canonical: `${BASE_URL}/services/server-cloud`,
    ogTitle: "Synology NAS & Server Installation | SightLine Networks",
    ogDescription: "Synology NAS, on-premise servers, local backup, cloud migration, and hybrid infrastructure for Tampa Bay businesses.",
    keywords: "Synology installer Tampa, Synology NAS, Synology DSM, local backup Tampa Bay, business backup, server installation Tampa, on-premise server, cloud migration, disaster recovery, virtualization, hybrid cloud, business storage",
  },
  "website-development": {
    title: "Website Development in Tampa Bay | Business Websites & SEO | SightLine Networks",
    description: "Professional website development for Tampa Bay businesses. Responsive design, SEO optimization, lead generation, CMS management, and ongoing support. Pair with managed hosting.",
    canonical: `${BASE_URL}/services/website-development`,
    ogTitle: "Website Development | SightLine Networks",
    ogDescription: "Modern business websites with responsive design, SEO, lead generation, and ongoing support in Tampa Bay.",
  },
  "it-consulting": {
    title: "IT Consulting & Strategy in Tampa Bay | Technology Assessments | SightLine Networks",
    description: "Strategic IT consulting for Tampa Bay businesses. Technology assessments, infrastructure planning, compliance guidance, vendor evaluation, and IT roadmap development.",
    canonical: `${BASE_URL}/services/it-consulting`,
    ogTitle: "IT Consulting & Strategy | SightLine Networks",
    ogDescription: "Technology assessments, IT planning, compliance guidance, and strategic consulting for Tampa Bay businesses.",
  },
};

// =========================================================================
// SEO & TRACKING UTILITIES
// =========================================================================

function upsertMeta(
  selector: string,
  attr: "name" | "property",
  key: string,
  content: string
) {
  let el = document.querySelector(selector) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel: string, href: string) {
  let el = document.querySelector(
    `link[rel="${rel}"]`
  ) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function buildSchema(page: PageKey) {
  const business = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "@id": `${BASE_URL}/#business`,
    name: "SightLine Networks",
    url: BASE_URL,
    telephone: "+1-727-283-6452",
    email: EMAIL,
    areaServed: [
      "Tampa",
      "Largo",
      "Clearwater",
      "St. Petersburg",
      "Pinellas Park",
      "Brandon",
      "Tampa Bay",
    ],
    description: SEO_CONFIG[page].description,
    address: {
      "@type": "PostalAddress",
      addressLocality: "Clearwater",
      addressRegion: "FL",
      addressCountry: "US",
    },
    serviceType: [
      "Managed IT Services",
      "Network Installation",
      "Business Web Hosting",
      "Website Development",
      "Security Camera Systems",
      "Server and Cloud Support",
      "IT Consulting",
      "Ubiquiti UniFi Installation",
      "UniFi Protect Installation",
      "Cisco Network Installation",
      "Synology NAS Installation",
      "VOIP Phone Systems",
    ],
    brand: [
      "Ubiquiti", "UniFi", "UniFi Protect", "UniFi Network", "UniFi Access",
      "Cisco", "Cisco Meraki", "Synology",
      "Hikvision", "Axis Communications", "Dahua", "Amcrest", "Reolink",
      "Yealink", "Poly", "Grandstream",
    ].map((b) => ({ "@type": "Brand", name: b })),
  };

  if (page === "hosting") {
    return {
      "@context": "https://schema.org",
      "@type": "Service",
      name: "Business Web Hosting",
      provider: { "@type": "ProfessionalService", "@id": `${BASE_URL}/#business` },
      areaServed: "Tampa Bay",
      description: SEO_CONFIG.hosting.description,
      offers: [
        { "@type": "Offer", name: "Starter Hosting", price: "29", priceCurrency: "USD" },
        { "@type": "Offer", name: "Business Hosting", price: "59", priceCurrency: "USD" },
        { "@type": "Offer", name: "Pro Hosting", price: "99", priceCurrency: "USD" },
      ],
    };
  }

  const servicePages: PageKey[] = ["managed-it", "network-installation", "security-cameras", "server-cloud", "website-development", "it-consulting"];
  if (servicePages.includes(page)) {
    const brandsByPage: Partial<Record<PageKey, string[]>> = {
      "network-installation": ["Ubiquiti UniFi", "Ubiquiti", "Cisco", "Cisco Meraki"],
      "security-cameras": ["Ubiquiti UniFi Protect", "Hikvision", "Axis Communications", "Dahua", "Amcrest", "Reolink"],
      "server-cloud": ["Synology", "Dell", "HPE"],
      "managed-it": ["Ubiquiti UniFi", "Cisco", "Synology", "Microsoft"],
    };
    const brandList = brandsByPage[page];
    return {
      "@context": "https://schema.org",
      "@type": "Service",
      name: SEO_CONFIG[page].ogTitle.replace(" | SightLine Networks", ""),
      provider: { "@type": "ProfessionalService", "@id": `${BASE_URL}/#business` },
      areaServed: "Tampa Bay",
      description: SEO_CONFIG[page].description,
      ...(brandList && {
        brand: brandList.map((b) => ({ "@type": "Brand", name: b })),
      }),
    };
  }

  if (page === "areas") {
    return { ...business, "@type": "LocalBusiness" };
  }

  return business;
}

function SeoManager({ page }: { page: PageKey }) {
  useEffect(() => {
    const c = SEO_CONFIG[page];
    document.title = c.title;
    upsertMeta('meta[name="description"]', "name", "description", c.description);
    if (c.keywords) {
      upsertMeta('meta[name="keywords"]', "name", "keywords", c.keywords);
    }
    upsertMeta('meta[property="og:title"]', "property", "og:title", c.ogTitle);
    upsertMeta('meta[property="og:description"]', "property", "og:description", c.ogDescription);
    upsertMeta('meta[property="og:type"]', "property", "og:type", "website");
    upsertMeta('meta[property="og:url"]', "property", "og:url", c.canonical);
    upsertMeta('meta[name="twitter:card"]', "name", "twitter:card", "summary_large_image");
    upsertMeta('meta[name="twitter:title"]', "name", "twitter:title", c.ogTitle);
    upsertMeta('meta[name="twitter:description"]', "name", "twitter:description", c.ogDescription);
    upsertLink("canonical", c.canonical);

    // Home page already ships an enriched LocalBusiness JSON-LD block in
    // public/index.html, so we skip dynamic injection on home to avoid
    // duplicate schema. Other pages get their schema injected here.
    const existing = document.getElementById("sln-schema") as HTMLScriptElement | null;
    if (page === "home") {
      if (existing) existing.remove();
    } else {
      const tag = existing ?? document.createElement("script");
      if (!existing) {
        tag.type = "application/ld+json";
        tag.id = "sln-schema";
        document.head.appendChild(tag);
      }
      tag.textContent = JSON.stringify(buildSchema(page));
    }
  }, [page]);

  return null;
}

/** Push CTA events to GTM dataLayer and GA4 gtag */
function trackCta(action: string, label: string, page: PageKey) {
  if (typeof window === "undefined") return;
  const w = window as any;
  w.dataLayer = w.dataLayer || [];
  w.dataLayer.push({ event: "cta_click", action, label, page, timestamp: new Date().toISOString() });
  if (typeof w.gtag === "function") {
    w.gtag("event", action, { event_category: "engagement", event_label: label, page_location: SEO_CONFIG[page].canonical });
  }
}

// =========================================================================
// SHARED UI COMPONENTS
// =========================================================================

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-sm font-semibold uppercase tracking-[0.18em] text-sky-600">
      {children}
    </p>
  );
}

function FeatureCard({
  icon: Icon,
  title,
  text,
  onClick,
}: {
  icon: typeof Globe;
  title: string;
  text: string;
  onClick?: () => void;
}) {
  return (
    <article className="group flex flex-col rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md">
      <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-sky-50 text-sky-600 ring-1 ring-sky-100">
        <Icon className="h-5 w-5" />
      </div>
      <h3 className="text-lg font-semibold tracking-tight text-slate-900">
        {title}
      </h3>
      <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600">
        {text}
      </p>
      {onClick && (
        <button
          onClick={onClick}
          className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-sky-600 transition hover:text-sky-700"
        >
          Learn more{" "}
          <ChevronRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
        </button>
      )}
    </article>
  );
}

function StatBlock({
  value,
  label,
}: {
  value: string;
  label: string;
}) {
  return (
    <div className="text-center">
      <div className="text-3xl font-bold tracking-tight text-slate-900 md:text-4xl">
        {value}
      </div>
      <div className="mt-1 text-sm text-slate-500">{label}</div>
    </div>
  );
}

function CtaButton({
  children,
  onClick,
  variant = "primary",
  ctaName,
  page,
  className = "",
  type = "button",
}: {
  children: React.ReactNode;
  onClick?: () => void;
  variant?: "primary" | "secondary" | "white";
  ctaName?: string;
  page?: PageKey;
  className?: string;
  type?: "button" | "submit";
}) {
  const base = "inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-medium transition focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2";
  const variants = {
    primary: "bg-sky-600 text-white shadow-sm hover:bg-sky-700",
    secondary: "border border-slate-300 bg-white text-slate-700 hover:bg-slate-50",
    white: "bg-white text-sky-700 shadow-sm hover:bg-sky-50",
  };
  return (
    <button
      type={type}
      onClick={onClick}
      data-cta={ctaName}
      data-page={page}
      className={`${base} ${variants[variant]} ${className}`}
    >
      {children}
    </button>
  );
}

// =========================================================================
// HEADER
// =========================================================================

function Header({ setPage, page, mobileOpen, setMobileOpen }: HeaderProps) {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`sticky top-0 z-50 border-b bg-white/95 backdrop-blur transition-shadow ${
        scrolled ? "border-slate-200 shadow-sm" : "border-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
        {/* Logo */}
        <button onClick={() => setPage("home")} aria-label="SightLine Networks home" className="shrink-0">
          <img
            src={`${process.env.PUBLIC_URL}/sightline-logo-white.png`}
            alt="SightLine Networks"
            className="h-20 w-auto"
          />
        </button>

        {/* Desktop nav */}
        <nav aria-label="Primary navigation" className="hidden items-center gap-1 lg:flex">
          <button
            onClick={() => setPage("home")}
            className={`rounded-lg px-3.5 py-2 text-sm font-medium transition ${
              page === "home"
                ? "bg-slate-100 text-slate-900"
                : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
            }`}
          >
            Home
          </button>
          {NAV_ITEMS.map(({ key, label }) =>
            key === "hosting" ? (
              <a
                key={key}
                href="https://host.sightlinenetworks.com"
                className="rounded-lg px-3.5 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-50 hover:text-slate-900"
              >
                {label}
              </a>
            ) : (
              <button
                key={key}
                onClick={() => setPage(key)}
                className={`rounded-lg px-3.5 py-2 text-sm font-medium transition ${
                  page === key
                    ? "bg-slate-100 text-slate-900"
                    : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                }`}
              >
                {label}
              </button>
            )
          )}
        </nav>

        {/* Desktop CTA cluster */}
        <div className="hidden items-center gap-3 lg:flex">
          <a
            href={PHONE_HREF}
            className="flex items-center gap-1.5 text-sm font-medium text-slate-600 transition hover:text-slate-900"
            onClick={() => trackCta("phone_click", "header_phone", page)}
          >
            <Phone className="h-4 w-4" />
            {PHONE}
          </a>
          <CtaButton
            onClick={() => {
              trackCta("quote_click", "header_cta", page);
              setPage("contact");
            }}
            ctaName="header-quote"
            page={page}
          >
            Get a Free Quote
          </CtaButton>
        </div>

        {/* Mobile toggle */}
        <button
          className="rounded-lg border border-slate-200 p-2 lg:hidden"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label={mobileOpen ? "Close menu" : "Open menu"}
        >
          {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="border-t border-slate-200 bg-white lg:hidden">
          <nav className="mx-auto flex max-w-7xl flex-col px-6 py-4" aria-label="Mobile navigation">
            <button
              onClick={() => { setPage("home"); setMobileOpen(false); }}
              className="rounded-lg px-3 py-3 text-left text-sm font-medium text-slate-700 transition hover:bg-slate-50"
            >
              Home
            </button>
            {NAV_ITEMS.map(({ key, label }) =>
              key === "hosting" ? (
                <a
                  key={key}
                  href="https://host.sightlinenetworks.com"
                  className="rounded-lg px-3 py-3 text-left text-sm font-medium text-slate-700 transition hover:bg-slate-50"
                >
                  {label}
                </a>
              ) : (
                <button
                  key={key}
                  onClick={() => { setPage(key); setMobileOpen(false); }}
                  className="rounded-lg px-3 py-3 text-left text-sm font-medium text-slate-700 transition hover:bg-slate-50"
                >
                  {label}
                </button>
              )
            )}
            <div className="mt-3 flex flex-col gap-2 border-t border-slate-100 pt-3">
              <a
                href={PHONE_HREF}
                className="flex items-center gap-2 rounded-lg px-3 py-3 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
              >
                <Phone className="h-4 w-4" /> {PHONE}
              </a>
              <CtaButton
                onClick={() => {
                  trackCta("quote_click", "mobile_cta", page);
                  setPage("contact");
                  setMobileOpen(false);
                }}
                ctaName="mobile-quote"
                page={page}
                className="w-full"
              >
                Get a Free Quote
              </CtaButton>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}

// =========================================================================
// FOOTER
// =========================================================================

function Footer({ setPage }: { setPage: (page: PageKey) => void }) {
  return (
    <footer className="border-t border-slate-800 bg-slate-950 text-slate-300">
      <div className="mx-auto max-w-7xl px-6 py-14">
        <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-4">
          {/* Brand */}
          <div>
            <img
              src={`${process.env.PUBLIC_URL}/sightline-logo.png`}
              alt="SightLine Networks"
              className="h-20 w-auto"
            />
            <p className="mt-4 text-sm leading-relaxed text-slate-400">
              Managed IT services, business web hosting, network installation,
              security camera systems, and website development for companies
              across the Tampa Bay area.
            </p>
            <div className="mt-5 flex flex-col gap-2 text-sm">
              <a href={PHONE_HREF} className="flex items-center gap-2 text-slate-400 transition hover:text-white">
                <Phone className="h-4 w-4" /> {PHONE}
              </a>
              <a href={`mailto:${EMAIL}`} className="flex items-center gap-2 text-slate-400 transition hover:text-white">
                <Mail className="h-4 w-4" /> {EMAIL}
              </a>
            </div>
          </div>

          {/* Services links */}
          <div>
            <h2 className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-300">
              Services
            </h2>
            <nav className="mt-4 flex flex-col gap-2.5 text-sm" aria-label="Footer services">
              <button className="text-left text-slate-400 transition hover:text-white" onClick={() => setPage("managed-it")}>Managed IT Support</button>
              <button className="text-left text-slate-400 transition hover:text-white" onClick={() => setPage("network-installation")}>Network Installation</button>
              <button className="text-left text-slate-400 transition hover:text-white" onClick={() => setPage("security-cameras")}>Security Cameras</button>
              <button className="text-left text-slate-400 transition hover:text-white" onClick={() => setPage("website-development")}>Website Development</button>
              <button className="text-left text-slate-400 transition hover:text-white" onClick={() => setPage("hosting")}>Business Web Hosting</button>
              <button className="text-left text-slate-400 transition hover:text-white" onClick={() => setPage("server-cloud")}>Server &amp; Cloud</button>
            </nav>
          </div>

          {/* Company links */}
          <div>
            <h2 className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-300">
              Company
            </h2>
            <nav className="mt-4 flex flex-col gap-2.5 text-sm" aria-label="Footer company">
              <button className="text-left text-slate-400 transition hover:text-white" onClick={() => setPage("about")}>About Us</button>
              <button className="text-left text-slate-400 transition hover:text-white" onClick={() => setPage("solutions")}>Solutions</button>
              <button className="text-left text-slate-400 transition hover:text-white" onClick={() => setPage("areas")}>Areas Served</button>
              <button className="text-left text-slate-400 transition hover:text-white" onClick={() => setPage("contact")}>Contact</button>
            </nav>
          </div>

          {/* Areas served */}
          <div>
            <h2 className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-300">
              Areas Served
            </h2>
            <div className="mt-4 flex flex-col gap-2.5 text-sm text-slate-400">
              <span>Tampa</span>
              <span>Clearwater</span>
              <span>Largo</span>
              <span>St. Petersburg</span>
              <span>Pinellas Park</span>
              <span>Brandon</span>
            </div>
          </div>
        </div>

        <div className="mt-12 flex flex-col items-center justify-between gap-4 border-t border-slate-800 pt-8 md:flex-row">
          <p className="text-xs text-slate-500">
            &copy; {new Date().getFullYear()} SightLine Networks. All rights
            reserved. Clearwater, FL &mdash; Serving Tampa Bay and beyond.
          </p>
          <div className="flex gap-4 text-xs text-slate-500">
            <button className="transition hover:text-slate-300" onClick={() => setPage("contact")}>Privacy Policy</button>
            <button className="transition hover:text-slate-300" onClick={() => setPage("contact")}>Terms of Service</button>
          </div>
        </div>
      </div>
    </footer>
  );
}

// =========================================================================
// HOME PAGE
// =========================================================================

function HomePage({ setPage }: { setPage: (page: PageKey) => void }) {
  return (
    <main>
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-slate-100 bg-gradient-to-b from-sky-50/60 via-white to-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_30%,rgba(14,165,233,0.08),transparent_50%)]" />
        <div className="relative mx-auto max-w-7xl px-6 pb-20 pt-16 lg:pb-28 lg:pt-24">
          <div className="grid gap-14 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-sky-200 bg-sky-50 px-3.5 py-1.5 text-xs font-semibold uppercase tracking-wide text-sky-700">
                <MapPin className="h-3.5 w-3.5" />
                Serving Tampa Bay businesses
              </div>

              <h1 className="mt-6 text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
                IT services and managed hosting built for{" "}
                <span className="text-sky-600">growing businesses</span>
              </h1>

              <p className="mt-6 max-w-xl text-lg leading-relaxed text-slate-600">
                From network installation and security cameras to business web
                hosting and website development, SightLine Networks gives your
                company the technology backbone it needs to operate without
                interruption.
              </p>

              <div className="mt-8 flex flex-wrap gap-3">
                <CtaButton
                  onClick={() => {
                    trackCta("quote_click", "hero_primary", "home");
                    setPage("contact");
                  }}
                  ctaName="hero-quote"
                  page="home"
                >
                  Get a Free Quote <ArrowRight className="h-4 w-4" />
                </CtaButton>
                <CtaButton
                  variant="secondary"
                  onClick={() => {
                    trackCta("services_click", "hero_secondary", "home");
                    setPage("services");
                  }}
                  ctaName="hero-services"
                  page="home"
                >
                  Explore Our Services
                </CtaButton>
              </div>

              {/* Trust bar */}
              <div className="mt-10 flex flex-wrap items-center gap-6 border-t border-slate-200 pt-6 sm:gap-10">
                <StatBlock value="500+" label="Projects completed" />
                <StatBlock value="24/7" label="Monitoring & support" />
                <StatBlock value="99.9%" label="Hosting uptime" />
              </div>
            </div>

            {/* Solution finder widget */}
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-lg shadow-slate-200/50">
              <div className="flex items-center justify-between pb-4">
                <div>
                  <div className="text-base font-semibold text-slate-900">
                    What does your business need?
                  </div>
                  <div className="mt-0.5 text-xs text-slate-500">
                    Pick one to get started
                  </div>
                </div>
                <div className="rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">
                  Quick start
                </div>
              </div>

              <div className="space-y-2.5">
                {[
                  { label: "Better Wi-Fi and network performance", target: "network-installation" as PageKey, icon: Wifi },
                  { label: "Security cameras for the business", target: "security-cameras" as PageKey, icon: Camera },
                  { label: "A professional website or redesign", target: "website-development" as PageKey, icon: Monitor },
                  { label: "Reliable web hosting with support", target: "hosting" as PageKey, icon: Cloud },
                  { label: "Ongoing managed IT support", target: "managed-it" as PageKey, icon: Headphones },
                  { label: "Server or cloud infrastructure", target: "server-cloud" as PageKey, icon: Server },
                ].map(({ label, target, icon: ItemIcon }) => (
                  <button
                    key={label}
                    onClick={() => {
                      trackCta("solution_selector", label, "home");
                      setPage(target);
                    }}
                    className="flex w-full items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3.5 text-left text-sm text-slate-700 transition hover:border-sky-300 hover:bg-sky-50"
                  >
                    <ItemIcon className="h-4 w-4 shrink-0 text-sky-600" />
                    <span className="flex-1">{label}</span>
                    <ChevronRight className="h-4 w-4 shrink-0 text-slate-400" />
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core services */}
      <section className="mx-auto max-w-7xl px-6 py-20">
        <div className="mb-12 max-w-2xl">
          <SectionLabel>What we do</SectionLabel>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            Technology services that keep your business running
          </h2>
          <p className="mt-4 text-slate-600 leading-relaxed">
            We handle IT infrastructure, networking, security, hosting, and web
            development so you can focus on running your business.
          </p>
        </div>

        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          <FeatureCard
            icon={Globe}
            title="Network Installation"
            text="Structured cabling, switches, access points, and enterprise-grade Wi-Fi designed for your facility and growth plans."
            onClick={() => setPage("network-installation")}
          />
          <FeatureCard
            icon={Shield}
            title="Security Camera Systems"
            text="Professional surveillance design and installation with remote viewing, cloud storage, and HD video across your property."
            onClick={() => setPage("security-cameras")}
          />
          <FeatureCard
            icon={Server}
            title="Server & Cloud"
            text="On-premise server installation, cloud migration, backup solutions, and hybrid infrastructure for reliable operations."
            onClick={() => setPage("server-cloud")}
          />
          <FeatureCard
            icon={Monitor}
            title="Website Development"
            text="Responsive business websites with modern design, SEO foundations, lead generation, and ongoing content management."
            onClick={() => setPage("website-development")}
          />
          <FeatureCard
            icon={Lock}
            title="Business Web Hosting"
            text="Managed hosting with SSL, daily backups, performance monitoring, and support — so your website is always online."
            onClick={() => setPage("hosting")}
          />
          <FeatureCard
            icon={Headphones}
            title="Managed IT Support"
            text="Proactive monitoring, helpdesk support, software updates, and technology guidance from a local Tampa Bay team."
            onClick={() => setPage("managed-it")}
          />
        </div>
      </section>

      {/* Why choose us */}
      <section className="border-y border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-7xl px-6 py-20">
          <div className="mb-12 max-w-2xl">
            <SectionLabel>Why SightLine Networks</SectionLabel>
            <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              A technology partner you can count on
            </h2>
          </div>

          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { icon: MapPin, title: "Local Tampa Bay team", text: "On-site support and in-person consultations across the Tampa Bay area. We know the local business landscape." },
              { icon: Clock3, title: "Fast response times", text: "Same-day support for urgent issues. We monitor systems 24/7 and resolve problems before they impact your operations." },
              { icon: BadgeCheck, title: "Certified professionals", text: "Our engineers hold certifications from Cisco, Microsoft, VMware, and other leading technology vendors." },
              { icon: LineChart, title: "Transparent pricing", text: "No hidden fees or surprise charges. Clear proposals, predictable monthly costs, and flexible service agreements." },
            ].map(({ icon: Icon, title, text }) => (
              <div key={title} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-sky-50 text-sky-600 ring-1 ring-sky-100">
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="text-base font-semibold text-slate-900">{title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Equipment brands */}
      <section className="border-t border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-6 py-20">
          <div className="mb-12 max-w-3xl">
            <SectionLabel>Ubiquiti UniFi, Cisco & Synology specialists</SectionLabel>
            <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              Certified installers of UniFi Protect, UniFi Network, Cisco & Synology in Tampa Bay
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              SightLine Networks designs, installs, and supports business systems built on Ubiquiti UniFi (UniFi Network, UniFi Protect, and UniFi Access),
              Cisco networking and VOIP equipment, and Synology NAS for local backup and storage. We also install IP security cameras from
              Hikvision, Axis, and Dahua, plus VOIP phone systems from Yealink, Poly, and Grandstream.
            </p>
          </div>

          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { name: "Ubiquiti UniFi", category: "UniFi Network, UniFi Protect & UniFi Access — cameras, networking & security", logoUrl: `${process.env.PUBLIC_URL}/brands/unifi.png` },
              { name: "Cisco", category: "Cisco & Meraki networking, switches and VOIP" },
              { name: "Synology", category: "Synology NAS — local storage, backup & DSM" },
              { name: "Hikvision", category: "IP security cameras & NVR systems", logoUrl: `${process.env.PUBLIC_URL}/brands/hikvision.png` },
              { name: "Axis Communications", category: "Enterprise IP cameras", logoUrl: `${process.env.PUBLIC_URL}/brands/axis.png` },
              { name: "Dahua", category: "IP cameras & surveillance", logoUrl: `${process.env.PUBLIC_URL}/brands/dahua.png` },
              { name: "Yealink", category: "VOIP desk phones & conference phones" },
              { name: "Poly (Polycom)", category: "VOIP & video conferencing" },
              { name: "Grandstream", category: "VOIP phones, gateways & PBX" },
            ].map(({ name, category, logoUrl }) => (
              <div
                key={name}
                className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition hover:border-sky-300 hover:shadow-md"
              >
                <div className="flex h-16 w-24 shrink-0 items-center justify-center rounded-xl bg-slate-50 px-3">
                  {logoUrl ? (
                    <img
                      src={logoUrl}
                      alt={`${name} logo`}
                      className="h-10 w-auto max-w-[80px] object-contain"
                      loading="lazy"
                    />
                  ) : (
                    <span className="text-base font-bold tracking-tight text-slate-700">
                      {name}
                    </span>
                  )}
                </div>
                <div>
                  <div className="text-base font-semibold text-slate-900">{name}</div>
                  <div className="mt-0.5 text-sm text-slate-600">{category}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ — mirrors FAQPage JSON-LD in public/index.html */}
      <section className="border-t border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-4xl px-6 py-20">
          <div className="mb-10 max-w-2xl">
            <SectionLabel>Frequently asked questions</SectionLabel>
            <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              Common questions about our brands &amp; services
            </h2>
          </div>
          <div className="divide-y divide-slate-200 rounded-2xl border border-slate-200 bg-white">
            {HOME_FAQS.map(({ q, a }) => (
              <details key={q} className="group p-6">
                <summary className="flex cursor-pointer list-none items-start justify-between gap-4 text-base font-semibold text-slate-900 marker:hidden">
                  <span>{q}</span>
                  <ChevronRight className="mt-0.5 h-5 w-5 shrink-0 text-sky-600 transition-transform group-open:rotate-90" />
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-slate-600">{a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="border-t border-slate-200 bg-sky-600">
        <div className="mx-auto max-w-7xl px-6 py-16 text-center">
          <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">
            Ready to improve your business technology?
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-sky-100">
            Tell us about your project and get a free, no-obligation quote from
            our Tampa Bay team. Most quotes returned within one business day.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <CtaButton
              variant="white"
              onClick={() => {
                trackCta("quote_click", "home_bottom_cta", "home");
                setPage("contact");
              }}
              ctaName="home-bottom-quote"
              page="home"
            >
              Get a Free Quote <ArrowRight className="h-4 w-4" />
            </CtaButton>
            <a
              href={PHONE_HREF}
              className="inline-flex items-center gap-2 rounded-xl border border-sky-500 px-5 py-3 text-sm font-medium text-white transition hover:bg-sky-700"
              onClick={() => trackCta("phone_click", "home_bottom_phone", "home")}
            >
              <Phone className="h-4 w-4" /> Call {PHONE}
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}

// =========================================================================
// SERVICES PAGE
// =========================================================================

function ServicesPage({ setPage }: { setPage: (page: PageKey) => void }) {
  const services: Array<{ icon: typeof Headphones; title: string; text: string; features: string[]; page: PageKey }> = [
    {
      icon: Headphones,
      title: "Managed IT Support",
      text: "Proactive monitoring, helpdesk support, patch management, and strategic technology guidance. We keep your systems running so your team stays productive.",
      features: ["24/7 monitoring", "Remote & on-site support", "Vendor management", "Technology roadmaps"],
      page: "managed-it",
    },
    {
      icon: Globe,
      title: "Network Installation",
      text: "Professional network design and deployment including structured cabling, switches, firewalls, access points, and enterprise-grade Wi-Fi coverage.",
      features: ["Structured cabling", "Wireless design", "Firewall setup", "Network audits"],
      page: "network-installation",
    },
    {
      icon: Camera,
      title: "Security Camera Systems",
      text: "Commercial surveillance system design, installation, and support. HD and 4K cameras with remote viewing, cloud storage, and motion-triggered alerts.",
      features: ["HD/4K cameras", "Remote viewing", "Cloud recording", "Access control"],
      page: "security-cameras",
    },
    {
      icon: Server,
      title: "Server & Cloud Infrastructure",
      text: "On-premise server installation, cloud migration, hybrid environments, and disaster recovery planning for businesses that depend on data availability.",
      features: ["Server setup", "Cloud migration", "Backup & recovery", "Virtualization"],
      page: "server-cloud",
    },
    {
      icon: Monitor,
      title: "Website Development",
      text: "Modern, responsive business websites designed for performance, search visibility, and lead generation. Built to work across all devices with ongoing support available.",
      features: ["Responsive design", "SEO foundations", "Lead capture", "CMS management"],
      page: "website-development",
    },
    {
      icon: Lock,
      title: "Business Web Hosting",
      text: "Managed hosting plans built for company websites with free SSL, daily automated backups, uptime monitoring, and expert support when you need it.",
      features: ["Free SSL", "Daily backups", "99.9% uptime", "Managed support"],
      page: "hosting",
    },
    {
      icon: LineChart,
      title: "IT Consulting & Strategy",
      text: "Technology assessments, infrastructure planning, compliance guidance, and strategic IT roadmaps for businesses preparing for growth or change.",
      features: ["IT assessments", "Compliance guidance", "Vendor evaluation", "Budget planning"],
      page: "it-consulting",
    },
  ];

  return (
    <main>
      {/* Hero */}
      <section className="border-b border-slate-200 bg-gradient-to-b from-slate-50 to-white">
        <div className="mx-auto max-w-7xl px-6 py-16 lg:py-20">
          <div className="max-w-3xl">
            <SectionLabel>Our services</SectionLabel>
            <h1 className="mt-2 text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl">
              Business IT services for Tampa Bay companies
            </h1>
            <p className="mt-5 text-lg leading-relaxed text-slate-600">
              From managed IT support and network installation to security
              cameras, hosting, and website development — we provide the
              technology services your business needs to run smoothly and grow
              confidently.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <CtaButton
                onClick={() => {
                  trackCta("quote_click", "services_hero", "services");
                  setPage("contact");
                }}
                ctaName="services-quote"
                page="services"
              >
                Request a Quote
              </CtaButton>
              <CtaButton
                variant="secondary"
                onClick={() => setPage("hosting")}
              >
                View Hosting Plans
              </CtaButton>
            </div>
          </div>
        </div>
      </section>

      {/* Service cards */}
      <section className="mx-auto max-w-7xl px-6 py-20">
        <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
          {services.map((svc) => (
            <article
              key={svc.title}
              className="flex flex-col rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition hover:shadow-md"
            >
              <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-sky-50 text-sky-600 ring-1 ring-sky-100">
                <svc.icon className="h-5 w-5" />
              </div>
              <h2 className="text-xl font-semibold tracking-tight text-slate-900">
                {svc.title}
              </h2>
              <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600">
                {svc.text}
              </p>
              <ul className="mt-4 grid grid-cols-2 gap-2">
                {svc.features.map((f) => (
                  <li
                    key={f}
                    className="flex items-center gap-1.5 text-xs text-slate-500"
                  >
                    <Check className="h-3.5 w-3.5 text-sky-500" /> {f}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => setPage(svc.page)}
                data-cta="service-detail"
                data-service={svc.title}
                className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-sky-600 transition hover:text-sky-700"
              >
                {svc.title === "Business Web Hosting"
                  ? "View plans"
                  : "Learn more"}{" "}
                <ChevronRight className="h-4 w-4" />
              </button>
            </article>
          ))}
        </div>
      </section>

      {/* Cross-link CTA */}
      <section className="border-t border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-7xl px-6 py-16">
          <div className="grid gap-8 lg:grid-cols-[1.2fr_0.8fr] lg:items-center">
            <div>
              <h2 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
                Not sure which service you need?
              </h2>
              <p className="mt-3 text-slate-600 leading-relaxed">
                Our solutions page helps you find the right service based on the
                business problem you are trying to solve. Or talk to our team
                directly for a personalized recommendation.
              </p>
            </div>
            <div className="flex flex-wrap gap-3 lg:justify-end">
              <CtaButton onClick={() => setPage("solutions")}>
                Browse Solutions
              </CtaButton>
              <CtaButton
                variant="secondary"
                onClick={() => {
                  trackCta("quote_click", "services_bottom", "services");
                  setPage("contact");
                }}
                ctaName="services-bottom-quote"
                page="services"
              >
                Talk to Our Team
              </CtaButton>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

// =========================================================================
// HOSTING PAGE
// =========================================================================

function HostingPage({ setPage }: { setPage: (page: PageKey) => void }) {
  const [billing, setBilling] = useState<"monthly" | "annual">("annual");

  const plans = [
    {
      name: "Starter",
      monthlyPrice: 29,
      annualPrice: 290,
      annualMonthly: 24,
      description: "For small business websites, landing pages, and brochure sites that need reliable uptime and basic support.",
      features: [
        "1 website",
        "10 GB SSD storage",
        "Free SSL certificate",
        "Daily automated backups",
        "Uptime monitoring",
        "Email support",
        "99.9% uptime SLA",
      ],
      featured: false,
    },
    {
      name: "Business",
      monthlyPrice: 59,
      annualPrice: 590,
      annualMonthly: 49,
      description: "For growing business websites that need faster performance, priority support, and room to scale.",
      features: [
        "Up to 3 websites",
        "25 GB SSD storage",
        "Free SSL certificate",
        "Daily automated backups",
        "Uptime & performance monitoring",
        "Priority phone & email support",
        "Staging environment",
        "99.9% uptime SLA",
      ],
      featured: true,
    },
    {
      name: "Pro",
      monthlyPrice: 99,
      annualPrice: 990,
      annualMonthly: 83,
      description: "For high-traffic websites, custom applications, and businesses that need maximum performance and support.",
      features: [
        "Up to 10 websites",
        "50 GB SSD storage",
        "Free SSL certificate",
        "Daily automated backups",
        "Advanced performance monitoring",
        "Dedicated support manager",
        "Staging environment",
        "Disaster recovery",
        "99.99% uptime SLA",
      ],
      featured: false,
    },
  ];

  const faqItems = [
    {
      q: "What types of websites can I host?",
      a: "Our plans support WordPress sites, custom-built business websites, landing pages, e-commerce stores, and web applications. We optimize for the platform you use.",
    },
    {
      q: "Is SSL included with all plans?",
      a: "Yes. Every hosting plan includes a free SSL certificate that auto-renews, ensuring your site is always served over HTTPS for visitor trust and SEO.",
    },
    {
      q: "How do backups work?",
      a: "We run daily automated backups of your website files and databases. Business and Pro plans include one-click restore. Backups are stored off-server for safety.",
    },
    {
      q: "Can I bundle hosting with website development?",
      a: "Absolutely. Many of our clients pair a website build with ongoing hosting and managed support. We offer package pricing when you combine services.",
    },
    {
      q: "What happens if my site goes down?",
      a: "Our monitoring systems detect downtime within minutes and our team begins remediation immediately. Business and Pro plans include proactive alerting and faster response.",
    },
  ];

  return (
    <main>
      {/* Hero */}
      <section className="border-b border-slate-200 bg-gradient-to-b from-sky-50/50 to-white">
        <div className="mx-auto max-w-7xl px-6 py-16 text-center lg:py-20">
          <SectionLabel>Business web hosting</SectionLabel>
          <h1 className="mx-auto mt-3 max-w-3xl text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl">
            Fast, secure hosting with managed support for your business website
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-lg leading-relaxed text-slate-600">
            Every plan includes free SSL, daily backups, uptime monitoring, and
            hands-on support from our Tampa Bay team. Pick a plan and launch
            with confidence.
          </p>

          {/* Billing toggle */}
          <div className="mt-8 inline-flex items-center rounded-xl border border-slate-200 bg-white p-1 shadow-sm">
            <button
              onClick={() => setBilling("monthly")}
              className={`rounded-lg px-5 py-2.5 text-sm font-medium transition ${
                billing === "monthly"
                  ? "bg-sky-600 text-white shadow-sm"
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBilling("annual")}
              className={`rounded-lg px-5 py-2.5 text-sm font-medium transition ${
                billing === "annual"
                  ? "bg-sky-600 text-white shadow-sm"
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              Annual{" "}
              <span className={`ml-1 rounded-full px-2 py-0.5 text-xs font-semibold ${
                billing === "annual" ? "bg-sky-500 text-white" : "bg-emerald-50 text-emerald-700"
              }`}>
                Save 17%
              </span>
            </button>
          </div>
        </div>
      </section>

      {/* Pricing cards */}
      <section className="mx-auto max-w-7xl px-6 -mt-2 pb-20">
        <div className="grid gap-6 lg:grid-cols-3" aria-label="Hosting plans">
          {plans.map((plan) => {
            const price = billing === "annual" ? plan.annualMonthly : plan.monthlyPrice;
            const total = billing === "annual" ? plan.annualPrice : null;
            return (
              <article
                key={plan.name}
                className={`relative flex flex-col rounded-2xl border p-7 shadow-sm transition hover:shadow-md ${
                  plan.featured
                    ? "border-sky-300 bg-white shadow-sky-100/50 ring-1 ring-sky-200"
                    : "border-slate-200 bg-white"
                }`}
              >
                {plan.featured && (
                  <div className="absolute -top-3 left-6 rounded-full bg-sky-600 px-3 py-1 text-xs font-semibold text-white shadow-sm">
                    Most Popular
                  </div>
                )}
                <h2 className="text-xl font-bold text-slate-900">{plan.name}</h2>
                <p className="mt-2 text-sm text-slate-500">{plan.description}</p>

                <div className="mt-5 flex items-baseline gap-1">
                  <span className="text-4xl font-bold tracking-tight text-slate-900">
                    ${price}
                  </span>
                  <span className="text-sm text-slate-500">/mo</span>
                </div>
                {total && (
                  <p className="mt-1 text-xs text-slate-400">
                    ${total}/year billed annually
                  </p>
                )}

                <CtaButton
                  onClick={() => {
                    trackCta("hosting_plan_click", plan.name, "hosting");
                    setPage("contact");
                  }}
                  ctaName={`hosting-${plan.name.toLowerCase()}`}
                  page="hosting"
                  className={`mt-6 w-full ${plan.featured ? "" : ""}`}
                  variant={plan.featured ? "primary" : "secondary"}
                >
                  Choose {plan.name}
                </CtaButton>

                <ul className="mt-6 flex-1 space-y-3 border-t border-slate-100 pt-6">
                  {plan.features.map((f) => (
                    <li
                      key={f}
                      className="flex items-start gap-2 text-sm text-slate-600"
                    >
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-sky-500" />
                      {f}
                    </li>
                  ))}
                </ul>
              </article>
            );
          })}
        </div>

        {/* Guarantee */}
        <div className="mt-8 flex items-center justify-center gap-3 text-sm text-slate-500">
          <Shield className="h-5 w-5 text-sky-600" />
          <span>30-day money-back guarantee on all plans. No long-term contracts required.</span>
        </div>
      </section>

      {/* Feature highlights */}
      <section className="border-y border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-7xl px-6 py-20">
          <div className="mb-12 text-center">
            <h2 className="text-3xl font-bold tracking-tight text-slate-900">
              Everything included with your hosting
            </h2>
            <p className="mx-auto mt-3 max-w-xl text-slate-600">
              Enterprise-grade infrastructure and hands-on management for every plan.
            </p>
          </div>

          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { icon: Lock, title: "Free SSL Certificate", text: "Auto-renewing HTTPS encryption for every website on your plan." },
              { icon: HardDrive, title: "Daily Backups", text: "Automated daily backups stored off-server with easy restore options." },
              { icon: Zap, title: "Performance Optimized", text: "SSD storage, server-level caching, and CDN-ready infrastructure." },
              { icon: Headphones, title: "Expert Support", text: "Real people who understand your hosting, available when you need them." },
              { icon: Shield, title: "Security Monitoring", text: "Firewall protection, malware scanning, and proactive threat detection." },
              { icon: Globe, title: "99.9% Uptime SLA", text: "Guaranteed availability backed by infrastructure redundancy." },
              { icon: Wrench, title: "Managed Updates", text: "We handle CMS, plugin, and server updates so your site stays current." },
              { icon: ArrowRight, title: "Free Migration", text: "We move your existing website to our platform at no extra cost." },
            ].map(({ icon: Icon, title, text }) => (
              <div
                key={title}
                className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
              >
                <Icon className="mb-3 h-5 w-5 text-sky-600" />
                <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
                <p className="mt-1 text-xs leading-relaxed text-slate-500">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-7xl px-6 py-20">
        <div className="grid gap-12 lg:grid-cols-[0.4fr_0.6fr]">
          <div>
            <SectionLabel>Hosting FAQ</SectionLabel>
            <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900">
              Common questions about our hosting
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Can't find what you're looking for? Our team is happy to answer
              any hosting questions.
            </p>
            <CtaButton
              onClick={() => {
                trackCta("quote_click", "hosting_faq_cta", "hosting");
                setPage("contact");
              }}
              className="mt-6"
              ctaName="hosting-faq-contact"
              page="hosting"
            >
              Ask a Question
            </CtaButton>
          </div>

          <div className="space-y-6">
            {faqItems.map(({ q, a }) => (
              <div key={q} className="border-b border-slate-200 pb-6 last:border-0">
                <h3 className="text-base font-semibold text-slate-900">{q}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="border-t border-slate-200 bg-sky-600">
        <div className="mx-auto max-w-7xl px-6 py-14 text-center">
          <h2 className="text-2xl font-bold text-white sm:text-3xl">
            Ready to host your business website with confidence?
          </h2>
          <p className="mx-auto mt-3 max-w-lg text-sky-100">
            Choose a plan above or talk to our team about custom hosting needs.
            We also bundle hosting with website development.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <CtaButton
              variant="white"
              onClick={() => {
                trackCta("quote_click", "hosting_bottom", "hosting");
                setPage("contact");
              }}
              ctaName="hosting-bottom-quote"
              page="hosting"
            >
              Talk to Our Team
            </CtaButton>
            <CtaButton
              variant="secondary"
              onClick={() => setPage("services")}
              className="border-sky-500 bg-transparent text-white hover:bg-sky-700"
            >
              View All Services
            </CtaButton>
          </div>
        </div>
      </section>
    </main>
  );
}

// =========================================================================
// REUSABLE SERVICE PAGE TEMPLATE
// =========================================================================

function ServicePageTemplate({
  setPage,
  icon: Icon,
  title,
  subtitle,
  intro,
  features,
  benefits,
  relatedServices,
  faq,
  brands,
}: {
  setPage: (page: PageKey) => void;
  icon: typeof Globe;
  title: string;
  subtitle: string;
  intro: string;
  features: Array<{ title: string; text: string }>;
  benefits: string[];
  relatedServices: Array<{ label: string; page: PageKey }>;
  faq: Array<{ q: string; a: string }>;
  brands?: Array<{ name: string; logoUrl?: string }>;
}) {
  return (
    <main>
      {/* Hero */}
      <section className="border-b border-slate-200 bg-gradient-to-b from-sky-50/50 to-white">
        <div className="mx-auto max-w-7xl px-6 py-16 lg:py-20">
          <div className="max-w-3xl">
            <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-sky-100 text-sky-600">
              <Icon className="h-6 w-6" />
            </div>
            <h1 className="text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl">
              {title}
            </h1>
            <p className="mt-2 text-lg font-medium text-sky-600">{subtitle}</p>
            <p className="mt-5 text-lg leading-relaxed text-slate-600">{intro}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <CtaButton
                onClick={() => {
                  trackCta("quote_click", `${title}_hero`, "services");
                  setPage("contact");
                }}
                ctaName={`service-${title}-quote`}
                page="services"
              >
                Get a Free Quote <ArrowRight className="h-4 w-4" />
              </CtaButton>
              <CtaButton variant="secondary" onClick={() => setPage("services")}>
                View All Services
              </CtaButton>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="mx-auto max-w-7xl px-6 py-20">
        <div className="mb-12 max-w-2xl">
          <SectionLabel>What's included</SectionLabel>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900">
            How we deliver {title.toLowerCase()}
          </h2>
        </div>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {features.map((f) => (
            <div key={f.title} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <h3 className="text-base font-semibold text-slate-900">{f.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{f.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Brands */}
      {brands && brands.length > 0 && (
        <section className="border-b border-slate-200 bg-slate-50">
          <div className="mx-auto max-w-7xl px-6 py-14">
            <div className="mb-8 text-center">
              <SectionLabel>Brands we work with</SectionLabel>
              <h2 className="mt-2 text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
                Trusted brands we install and support
              </h2>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-6 md:gap-10">
              {brands.map((brand) => (
                <div
                  key={brand.name}
                  className="flex h-20 w-36 items-center justify-center rounded-xl border border-slate-200 bg-white px-4 shadow-sm transition hover:shadow-md"
                >
                  {brand.logoUrl ? (
                    <img
                      src={brand.logoUrl}
                      alt={`${brand.name} logo`}
                      className="h-10 w-auto max-w-[120px] object-contain"
                      loading="lazy"
                    />
                  ) : (
                    <span className="text-center text-sm font-bold tracking-tight text-slate-700">
                      {brand.name}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Benefits */}
      <section className="border-y border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-7xl px-6 py-16">
          <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
            <div>
              <h2 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
                Why businesses choose SightLine for {title.toLowerCase()}
              </h2>
              <ul className="mt-6 space-y-3">
                {benefits.map((b) => (
                  <li key={b} className="flex items-start gap-2.5 text-slate-600">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-sky-500" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-slate-900">Related services</h3>
              <p className="mt-2 text-sm text-slate-500">Explore other services that pair well.</p>
              <div className="mt-4 flex flex-col gap-2">
                {relatedServices.map((rs) => (
                  <button
                    key={rs.label}
                    onClick={() => setPage(rs.page)}
                    className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3 text-left text-sm text-slate-700 transition hover:border-sky-300 hover:bg-sky-50"
                  >
                    {rs.label}
                    <ChevronRight className="h-4 w-4 text-slate-400" />
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      {faq.length > 0 && (
        <section className="mx-auto max-w-7xl px-6 py-20">
          <div className="grid gap-12 lg:grid-cols-[0.4fr_0.6fr]">
            <div>
              <SectionLabel>FAQ</SectionLabel>
              <h2 className="mt-2 text-2xl font-bold tracking-tight text-slate-900">
                Common questions
              </h2>
              <CtaButton
                onClick={() => {
                  trackCta("quote_click", `${title}_faq`, "services");
                  setPage("contact");
                }}
                className="mt-6"
              >
                Ask a Question
              </CtaButton>
            </div>
            <div className="space-y-6">
              {faq.map(({ q, a }) => (
                <div key={q} className="border-b border-slate-200 pb-6 last:border-0">
                  <h3 className="text-base font-semibold text-slate-900">{q}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="border-t border-slate-200 bg-sky-600">
        <div className="mx-auto max-w-7xl px-6 py-14 text-center">
          <h2 className="text-2xl font-bold text-white sm:text-3xl">
            Ready to get started with {title.toLowerCase()}?
          </h2>
          <p className="mx-auto mt-3 max-w-lg text-sky-100">
            Tell us about your project and get a free quote from our Tampa Bay team.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <CtaButton
              variant="white"
              onClick={() => {
                trackCta("quote_click", `${title}_bottom`, "services");
                setPage("contact");
              }}
            >
              Get a Free Quote
            </CtaButton>
            <a
              href={PHONE_HREF}
              className="inline-flex items-center gap-2 rounded-xl border border-sky-500 px-5 py-3 text-sm font-medium text-white transition hover:bg-sky-700"
            >
              <Phone className="h-4 w-4" /> Call {PHONE}
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}

// =========================================================================
// INDIVIDUAL SERVICE PAGES
// =========================================================================

function ManagedItPage({ setPage }: { setPage: (page: PageKey) => void }) {
  return (
    <ServicePageTemplate
      setPage={setPage}
      icon={Headphones}
      title="Managed IT Support"
      subtitle="Proactive IT management for Tampa Bay businesses"
      intro="Stop putting out IT fires. Our managed IT service provides 24/7 monitoring, helpdesk support, cybersecurity, patch management, and strategic technology guidance — so your team stays productive and your systems stay secure."
      features={[
        { title: "24/7 Monitoring & Alerting", text: "We monitor your servers, network, and endpoints around the clock and respond to issues before they affect your operations." },
        { title: "Helpdesk Support", text: "Your team gets fast access to experienced technicians for troubleshooting, how-to questions, and technical guidance via phone, email, or remote session." },
        { title: "Patch Management", text: "We keep your operating systems, applications, and firmware up to date with tested patches that reduce vulnerabilities without disrupting work." },
        { title: "Cybersecurity", text: "Endpoint protection, email filtering, DNS security, and firewall management to defend your business against ransomware, phishing, and data breaches." },
        { title: "Vendor Management", text: "We coordinate with your ISP, software vendors, and hardware suppliers so you have a single point of contact for all technology issues." },
        { title: "Technology Roadmaps", text: "Quarterly business reviews with strategic recommendations on upgrades, budgets, and IT investments aligned to your business goals." },
      ]}
      benefits={[
        "Reduce downtime with proactive monitoring and faster issue resolution",
        "Predictable monthly costs instead of expensive break-fix surprises",
        "Local Tampa Bay team available for on-site support when needed",
        "Certified engineers with Cisco, Microsoft, and VMware expertise",
        "Single point of contact for all your technology needs",
        "Scalable support that grows with your business",
      ]}
      relatedServices={[
        { label: "Network Installation", page: "network-installation" },
        { label: "Server & Cloud Infrastructure", page: "server-cloud" },
        { label: "Security Cameras", page: "security-cameras" },
        { label: "IT Consulting & Strategy", page: "it-consulting" },
      ]}
      faq={[
        { q: "What does managed IT include?", a: "Our managed IT service covers 24/7 monitoring, helpdesk support, patch management, cybersecurity, vendor coordination, and strategic technology planning. We tailor the service to your business needs." },
        { q: "How fast is your response time?", a: "Critical issues receive immediate attention. Standard support requests are typically addressed within one hour during business hours, with 24/7 emergency coverage available." },
        { q: "Do you offer on-site support?", a: "Yes. While most issues are resolved remotely, we provide on-site support throughout the Tampa Bay area whenever hands-on work is needed." },
        { q: "Can you take over from our current IT provider?", a: "Absolutely. We handle the full onboarding process including documentation, asset inventory, credential transfer, and a transition plan to minimize disruption." },
      ]}
    />
  );
}

function NetworkInstallationPage({ setPage }: { setPage: (page: PageKey) => void }) {
  return (
    <ServicePageTemplate
      setPage={setPage}
      icon={Globe}
      title="Ubiquiti UniFi & Cisco Network Installation in Tampa Bay"
      subtitle="Certified UniFi and Cisco installers for business networks"
      intro="Whether you're building out a new office, upgrading aging infrastructure, or expanding to a new location, we design and install Ubiquiti UniFi and Cisco networks that are fast, reliable, and built to grow with your business — including UniFi access points, Cisco/Meraki switches, firewalls, and structured cabling."
      features={[
        { title: "Structured Cabling", text: "Cat6/Cat6A cabling runs, cable management, patch panels, and rack installations built to commercial standards with full documentation." },
        { title: "Enterprise Wi-Fi Design", text: "Site surveys, heat mapping, and access point deployment for seamless wireless coverage across your entire facility without dead zones." },
        { title: "Switches & Firewalls", text: "Managed switches, VLANs, QoS configuration, and next-gen firewall deployment for secure, segmented network infrastructure." },
        { title: "Network Audits", text: "Comprehensive assessment of your existing infrastructure to identify bottlenecks, security gaps, and upgrade opportunities." },
        { title: "VPN & Remote Access", text: "Secure site-to-site and remote access VPN configuration so your team can work from anywhere without compromising security." },
        { title: "Documentation & Training", text: "Complete network diagrams, port maps, credential documentation, and training for your team on day-to-day management." },
      ]}
      benefits={[
        "Professionally designed and installed by certified network engineers",
        "Clean, documented cabling that passes inspection and lasts for years",
        "Enterprise-grade Wi-Fi with complete coverage and zero dead zones",
        "Scalable design that accommodates business growth without rework",
        "Full documentation and network diagrams provided at handoff",
        "Ongoing support available through our managed IT service",
      ]}
      relatedServices={[
        { label: "Managed IT Support", page: "managed-it" },
        { label: "Security Cameras", page: "security-cameras" },
        { label: "Server & Cloud Infrastructure", page: "server-cloud" },
        { label: "Business Web Hosting", page: "hosting" },
      ]}
      faq={[
        { q: "Can you install cabling in an existing building?", a: "Yes. We install structured cabling in new and existing spaces, including retrofit projects. We work around your schedule to minimize disruption to daily operations." },
        { q: "What brands do you work with?", a: "We deploy equipment from Cisco, Ubiquiti (UniFi), Aruba, Fortinet, and other enterprise-grade vendors. We recommend the best fit based on your needs and budget." },
        { q: "How long does a typical installation take?", a: "Small office installations typically take 1-3 days. Larger projects with structured cabling and multiple floors may take 1-2 weeks. We provide a detailed timeline in your proposal." },
        { q: "Do you offer network maintenance after installation?", a: "Yes. We can provide ongoing managed IT support that includes network monitoring, firmware updates, and troubleshooting as part of our managed IT service." },
      ]}
    />
  );
}

function SecurityCamerasPage({ setPage }: { setPage: (page: PageKey) => void }) {
  return (
    <ServicePageTemplate
      setPage={setPage}
      icon={Camera}
      title="UniFi Protect, Hikvision &amp; Axis Security Camera Installation in Tampa Bay"
      subtitle="Certified IP camera installers for Tampa Bay businesses"
      intro="Protect your property, employees, and assets with professional-grade IP security camera systems from Ubiquiti UniFi Protect, Hikvision, Axis Communications, Dahua, Amcrest, and Reolink. We handle camera layout design, installation, NVR or cloud recording, remote viewing, and integration with access control."
      brands={[
        { name: "UniFi Protect", logoUrl: `${process.env.PUBLIC_URL}/brands/unifi.png` },
        { name: "Amcrest", logoUrl: `${process.env.PUBLIC_URL}/brands/amcrest.png` },
        { name: "Hikvision", logoUrl: `${process.env.PUBLIC_URL}/brands/hikvision.png` },
        { name: "Reolink", logoUrl: `${process.env.PUBLIC_URL}/brands/reolink.png` },
        { name: "Axis", logoUrl: `${process.env.PUBLIC_URL}/brands/axis.png` },
        { name: "Dahua", logoUrl: `${process.env.PUBLIC_URL}/brands/dahua.png` },
      ]}
      features={[
        { title: "Camera System Design", text: "We survey your property, identify coverage needs, and design a camera layout that eliminates blind spots and covers all critical areas." },
        { title: "HD & 4K IP Cameras", text: "Commercial-grade IP cameras with high-resolution video, night vision, wide-angle lenses, and weather-resistant housings for indoor and outdoor use." },
        { title: "Remote Viewing", text: "View live and recorded footage from any device — phone, tablet, or computer — from anywhere with an internet connection." },
        { title: "Cloud & Local Recording", text: "Flexible storage options including on-site NVR recording, cloud storage, or hybrid solutions based on your retention requirements." },
        { title: "Motion Detection & Alerts", text: "Intelligent motion-triggered recording with real-time push notifications so you know immediately when something needs attention." },
        { title: "Access Control Integration", text: "Pair cameras with door access systems, intercoms, and gate controls for a unified physical security solution." },
      ]}
      benefits={[
        "Deter theft, vandalism, and unauthorized access to your business",
        "Crystal clear HD and 4K footage for identification and evidence",
        "View your cameras from anywhere on your phone or computer",
        "Professional installation with clean cable runs and concealed wiring",
        "Scalable systems that expand easily as your needs grow",
        "Local Tampa Bay team for installation, training, and ongoing support",
      ]}
      relatedServices={[
        { label: "Network Installation", page: "network-installation" },
        { label: "Managed IT Support", page: "managed-it" },
        { label: "Server & Cloud Infrastructure", page: "server-cloud" },
        { label: "IT Consulting", page: "it-consulting" },
      ]}
      faq={[
        { q: "How many cameras do I need?", a: "It depends on your property size and coverage requirements. We provide a free site survey to determine the optimal number and placement of cameras for your business." },
        { q: "Can I view cameras on my phone?", a: "Yes. All of our camera systems include mobile app access so you can view live and recorded footage from your smartphone or tablet at any time." },
        { q: "How long is footage stored?", a: "Storage duration depends on your setup. Local NVR systems typically store 30-90 days of footage. Cloud storage plans offer flexible retention periods. We help you choose the right option." },
        { q: "Do you service existing camera systems?", a: "Yes. We can assess, repair, upgrade, or expand existing camera systems, regardless of the original installer. We also offer maintenance plans." },
      ]}
    />
  );
}

function ServerCloudPage({ setPage }: { setPage: (page: PageKey) => void }) {
  return (
    <ServicePageTemplate
      setPage={setPage}
      icon={Server}
      title="Synology NAS, Server &amp; Cloud Infrastructure in Tampa Bay"
      subtitle="Synology, on-premise servers, and cloud migration"
      intro="From Synology NAS installation and on-premise servers to cloud migration and hybrid environments, we build infrastructure that keeps your data available, your applications running, and your business protected against downtime and data loss — including local backup, virtualization, and disaster recovery."
      features={[
        { title: "Server Installation", text: "Physical server deployment including rack mounting, OS installation, Active Directory configuration, and integration with your existing network." },
        { title: "Cloud Migration", text: "Plan and execute migrations to AWS, Azure, Google Cloud, or Microsoft 365 with minimal downtime and full data verification." },
        { title: "Hybrid Environments", text: "Design infrastructure that balances on-premise and cloud resources for the best combination of performance, cost, and data control." },
        { title: "Backup & Disaster Recovery", text: "Automated backup solutions with offsite replication, tested recovery procedures, and documented RPO/RTO targets for your business." },
        { title: "Virtualization", text: "VMware and Hyper-V virtualization to consolidate hardware, improve resource utilization, and simplify server management." },
        { title: "Storage Solutions", text: "Network-attached storage (NAS), SAN configuration, and shared file systems designed for performance, redundancy, and easy collaboration." },
      ]}
      benefits={[
        "Infrastructure designed for your specific workloads and growth plans",
        "Tested backup and recovery procedures that protect your business data",
        "Cloud solutions that reduce hardware costs while improving reliability",
        "Virtualization that maximizes your existing hardware investment",
        "Ongoing monitoring and management through our managed IT service",
        "Local Tampa Bay engineers available for on-site server work",
      ]}
      relatedServices={[
        { label: "Managed IT Support", page: "managed-it" },
        { label: "Network Installation", page: "network-installation" },
        { label: "Business Web Hosting", page: "hosting" },
        { label: "IT Consulting", page: "it-consulting" },
      ]}
      faq={[
        { q: "Should I use on-premise servers or the cloud?", a: "It depends on your workloads, compliance requirements, and budget. Many businesses benefit from a hybrid approach. We assess your situation and recommend the best fit." },
        { q: "Can you migrate our data to the cloud?", a: "Yes. We plan and execute cloud migrations to platforms like Microsoft 365, Azure, AWS, and Google Cloud with full data verification and minimal disruption." },
        { q: "How do you handle backups?", a: "We implement automated backup solutions with local and offsite copies, scheduled verification, and documented recovery procedures tailored to your business needs." },
        { q: "Do you provide ongoing server management?", a: "Yes. Server monitoring, patching, and management are included in our managed IT service. We proactively maintain your infrastructure to prevent issues." },
      ]}
    />
  );
}

function WebsiteDevelopmentPage({ setPage }: { setPage: (page: PageKey) => void }) {
  return (
    <ServicePageTemplate
      setPage={setPage}
      icon={Monitor}
      title="Website Development"
      subtitle="Professional business websites for Tampa Bay companies"
      intro="Your website is often the first impression customers have of your business. We build modern, mobile-friendly websites designed to look professional, load fast, rank in search engines, and convert visitors into leads and customers."
      features={[
        { title: "Responsive Design", text: "Websites that look and work perfectly on every device — desktop, tablet, and mobile — with modern layouts and clean typography." },
        { title: "SEO Foundations", text: "Semantic HTML, meta tags, structured data, page speed optimization, and content structure that helps your site rank in Google and other search engines." },
        { title: "Lead Generation", text: "Contact forms, quote request CTAs, click-to-call buttons, and conversion tracking designed to turn website visitors into business inquiries." },
        { title: "CMS Management", text: "Content management systems that let you update text, images, and pages without needing a developer for everyday changes." },
        { title: "Performance Optimization", text: "Fast load times through image optimization, code splitting, caching strategies, and CDN delivery that improve both user experience and SEO." },
        { title: "Ongoing Support", text: "Post-launch support for updates, content changes, security patches, and feature additions — so your site stays current and secure." },
      ]}
      benefits={[
        "Professional design that builds trust with visitors and customers",
        "Mobile-first approach ensures great experience on all devices",
        "SEO foundations that help you rank for local and industry searches",
        "Lead generation features that convert traffic into business inquiries",
        "Pair with managed hosting for a complete website solution",
        "Local Tampa Bay team for ongoing support and collaboration",
      ]}
      relatedServices={[
        { label: "Business Web Hosting", page: "hosting" },
        { label: "Managed IT Support", page: "managed-it" },
        { label: "IT Consulting & Strategy", page: "it-consulting" },
        { label: "Network Installation", page: "network-installation" },
      ]}
      faq={[
        { q: "How long does it take to build a website?", a: "A typical business website takes 4-8 weeks from kickoff to launch. Timeline depends on scope, content readiness, and revision rounds. We provide a detailed schedule in your proposal." },
        { q: "Can you redesign my existing website?", a: "Yes. We handle full redesigns, migrations from other platforms, and incremental improvements to existing sites. We assess your current site and recommend the best approach." },
        { q: "Do you provide hosting for the websites you build?", a: "Yes. We offer managed hosting plans with SSL, daily backups, monitoring, and support — designed specifically for business websites. See our hosting page for plan details." },
        { q: "Will my website be optimized for search engines?", a: "Yes. Every website we build includes SEO foundations: semantic HTML, meta tags, structured data, fast load times, and mobile-friendly design. We also offer ongoing SEO services." },
      ]}
    />
  );
}

function ItConsultingPage({ setPage }: { setPage: (page: PageKey) => void }) {
  return (
    <ServicePageTemplate
      setPage={setPage}
      icon={LineChart}
      title="IT Consulting & Strategy"
      subtitle="Technology guidance for Tampa Bay businesses"
      intro="Make smarter technology decisions with strategic IT consulting. We provide assessments, infrastructure planning, compliance guidance, vendor evaluation, and IT roadmaps that align your technology investments with your business goals."
      features={[
        { title: "Technology Assessments", text: "Comprehensive evaluation of your current infrastructure, security posture, and technology stack with prioritized recommendations for improvement." },
        { title: "Infrastructure Planning", text: "Design technology architectures for new offices, expansions, relocations, or major upgrades — with clear specifications and budget estimates." },
        { title: "Compliance Guidance", text: "Navigate HIPAA, PCI-DSS, SOC 2, and industry-specific compliance requirements with documentation and controls tailored to your business." },
        { title: "Vendor Evaluation", text: "Objective assessment and comparison of technology vendors, platforms, and solutions so you make informed purchasing decisions." },
        { title: "Budget Planning", text: "IT budget development including capital vs. operational expense analysis, lifecycle planning, and ROI projections for proposed investments." },
        { title: "IT Roadmaps", text: "Multi-year technology plans that align IT investments with business growth, risk reduction, and operational improvement goals." },
      ]}
      benefits={[
        "Objective technology advice not tied to specific vendor relationships",
        "Clear, actionable recommendations with priority and budget context",
        "Compliance guidance that prepares your business for audits and certifications",
        "Strategic planning that prevents expensive reactive technology purchases",
        "Experience across industries including healthcare, professional services, and retail",
        "Ongoing advisory available as part of our managed IT service",
      ]}
      relatedServices={[
        { label: "Managed IT Support", page: "managed-it" },
        { label: "Network Installation", page: "network-installation" },
        { label: "Server & Cloud Infrastructure", page: "server-cloud" },
        { label: "Website Development", page: "website-development" },
      ]}
      faq={[
        { q: "When should I hire an IT consultant?", a: "Common triggers include office moves, rapid growth, compliance requirements, aging infrastructure, security concerns, or the need for a technology roadmap. If you're unsure, a free initial consultation can help." },
        { q: "Do you only consult, or do you implement too?", a: "Both. We provide strategic consulting as a standalone service, and we can also implement the solutions we recommend through our other service lines." },
        { q: "How is consulting priced?", a: "Consulting can be project-based (fixed scope and fee), hourly, or included as part of a managed IT agreement. We provide clear pricing in every proposal." },
        { q: "Can you help with compliance requirements?", a: "Yes. We help businesses understand and meet HIPAA, PCI-DSS, SOC 2, and other regulatory and industry compliance requirements through assessments, documentation, and controls implementation." },
      ]}
    />
  );
}

// =========================================================================
// SOLUTIONS PAGE
// =========================================================================

function SolutionsPage({ setPage }: { setPage: (page: PageKey) => void }) {
  const solutions = [
    {
      icon: Building2,
      title: "Setting up a new office",
      text: "Moving into a new space? We handle network cabling, Wi-Fi setup, phone systems, printers, security cameras, and workstation deployment so your team is productive from day one.",
      services: ["Network Installation", "Security Cameras", "Managed IT"],
      cta: "network-installation" as PageKey,
    },
    {
      icon: Wifi,
      title: "Fixing slow or unreliable Wi-Fi",
      text: "Dead zones, dropped connections, and slow speeds cost your team hours every week. We audit your wireless environment and deploy enterprise-grade access points for reliable coverage.",
      services: ["Network Installation", "Managed IT"],
      cta: "network-installation" as PageKey,
    },
    {
      icon: Camera,
      title: "Adding security cameras",
      text: "Protect your business with professional-grade surveillance. We design camera layouts, install equipment, set up remote viewing, and configure cloud or local recording.",
      services: ["Security Cameras", "Network Installation"],
      cta: "security-cameras" as PageKey,
    },
    {
      icon: Monitor,
      title: "Building a professional website",
      text: "Your website is often the first impression customers have of your business. We build modern, mobile-friendly sites designed to convert visitors into leads and customers.",
      services: ["Website Development", "Web Hosting", "SEO"],
      cta: "website-development" as PageKey,
    },
    {
      icon: Headphones,
      title: "Getting ongoing IT support",
      text: "Stop putting out fires. Our managed IT service provides proactive monitoring, helpdesk support, security updates, and technology planning so issues are handled before they impact your work.",
      services: ["Managed IT", "Server & Cloud"],
      cta: "managed-it" as PageKey,
    },
    {
      icon: Cloud,
      title: "Choosing the right web hosting",
      text: "Not all hosting is equal. We offer managed hosting plans with SSL, daily backups, monitoring, and real support — designed for business websites that need to stay online.",
      services: ["Web Hosting", "Website Development"],
      cta: "hosting" as PageKey,
    },
  ];

  return (
    <main>
      {/* Hero */}
      <section className="border-b border-slate-200 bg-gradient-to-b from-slate-50 to-white">
        <div className="mx-auto max-w-7xl px-6 py-16 lg:py-20">
          <div className="max-w-3xl">
            <SectionLabel>Solutions</SectionLabel>
            <h1 className="mt-2 text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl">
              Technology solutions for common business challenges
            </h1>
            <p className="mt-5 text-lg leading-relaxed text-slate-600">
              Start with the problem you are trying to solve. We will match you
              with the right service, explain what is involved, and give you a
              clear path to getting it done.
            </p>
          </div>
        </div>
      </section>

      {/* Solution cards */}
      <section className="mx-auto max-w-7xl px-6 py-20">
        <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
          {solutions.map((sol) => (
            <article
              key={sol.title}
              className="flex flex-col rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition hover:shadow-md"
            >
              <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-sky-50 text-sky-600 ring-1 ring-sky-100">
                <sol.icon className="h-5 w-5" />
              </div>
              <h2 className="text-lg font-semibold text-slate-900">
                {sol.title}
              </h2>
              <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600">
                {sol.text}
              </p>
              <div className="mt-4 flex flex-wrap gap-1.5">
                {sol.services.map((s) => (
                  <span
                    key={s}
                    className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-600"
                  >
                    {s}
                  </span>
                ))}
              </div>
              <button
                onClick={() => setPage(sol.cta)}
                className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-sky-600 transition hover:text-sky-700"
              >
                See how we help <ChevronRight className="h-4 w-4" />
              </button>
            </article>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-slate-200 bg-sky-50">
        <div className="mx-auto max-w-7xl px-6 py-14">
          <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-2xl font-bold tracking-tight text-slate-900">
                Need help choosing the right solution?
              </h2>
              <p className="mt-2 text-slate-600">
                Our team will assess your situation and recommend the best path
                forward — no obligation.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <CtaButton
                onClick={() => {
                  trackCta("quote_click", "solutions_cta", "solutions");
                  setPage("contact");
                }}
                ctaName="solutions-quote"
                page="solutions"
              >
                Get a Free Quote
              </CtaButton>
              <CtaButton variant="secondary" onClick={() => setPage("hosting")}>
                View Hosting Plans
              </CtaButton>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

// =========================================================================
// AREAS SERVED PAGE
// =========================================================================

function AreasPage({ setPage }: { setPage: (page: PageKey) => void }) {
  const areas = [
    {
      name: "Tampa",
      desc: "Managed IT, network installation, security cameras, web hosting, and website development for businesses throughout Tampa and Hillsborough County.",
    },
    {
      name: "Clearwater",
      desc: "Our home base. Full-service IT support, networking, surveillance, and web solutions for Clearwater businesses and surrounding areas.",
    },
    {
      name: "Largo",
      desc: "On-site and remote IT services, hosting, and technology support for businesses in Largo and mid-Pinellas County.",
    },
    {
      name: "St. Petersburg",
      desc: "Comprehensive business technology services including managed IT, web development, camera systems, and hosting for St. Pete companies.",
    },
    {
      name: "Pinellas Park",
      desc: "Network installation, IT support, security cameras, and business hosting services for Pinellas Park offices and commercial spaces.",
    },
    {
      name: "Brandon",
      desc: "Managed IT, networking, cloud infrastructure, and web services for businesses in the Brandon and eastern Hillsborough area.",
    },
  ];

  const serviceCategories = [
    "Managed IT support",
    "Network installation & Wi-Fi",
    "Security camera systems",
    "Business web hosting",
    "Website development",
    "Server & cloud infrastructure",
    "IT consulting & strategy",
    "Ongoing technology support",
  ];

  return (
    <main>
      {/* Hero */}
      <section className="border-b border-slate-200 bg-gradient-to-b from-slate-50 to-white">
        <div className="mx-auto max-w-7xl px-6 py-16 lg:py-20">
          <div className="max-w-3xl">
            <SectionLabel>Service coverage</SectionLabel>
            <h1 className="mt-2 text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl">
              IT services across the Tampa Bay area
            </h1>
            <p className="mt-5 text-lg leading-relaxed text-slate-600">
              SightLine Networks provides managed IT, web hosting, networking,
              security cameras, and website development to businesses throughout
              Tampa Bay. We offer both on-site and remote support.
            </p>
          </div>
        </div>
      </section>

      {/* Area cards */}
      <section className="mx-auto max-w-7xl px-6 py-20">
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {areas.map((area) => (
            <article
              key={area.name}
              className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
            >
              <div className="flex items-center gap-2">
                <MapPin className="h-5 w-5 text-sky-600" />
                <h2 className="text-lg font-bold text-slate-900">
                  {area.name}
                </h2>
              </div>
              <p className="mt-3 text-sm leading-relaxed text-slate-600">
                {area.desc}
              </p>
              <button
                onClick={() => {
                  trackCta("area_quote", area.name, "areas");
                  setPage("contact");
                }}
                className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-sky-600 transition hover:text-sky-700"
              >
                Get support in {area.name}{" "}
                <ChevronRight className="h-4 w-4" />
              </button>
            </article>
          ))}
        </div>
      </section>

      {/* Service categories */}
      <section className="border-y border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-7xl px-6 py-16">
          <h2 className="text-2xl font-bold tracking-tight text-slate-900">
            Services available across Tampa Bay
          </h2>
          <p className="mt-3 max-w-2xl text-slate-600">
            Every service we offer is available to businesses throughout our
            coverage area, with on-site support where needed.
          </p>
          <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {serviceCategories.map((svc) => (
              <div
                key={svc}
                className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700"
              >
                <CheckCircle2 className="h-4 w-4 shrink-0 text-sky-500" />
                {svc}
              </div>
            ))}
          </div>
          <CtaButton
            onClick={() => {
              trackCta("quote_click", "areas_bottom", "areas");
              setPage("contact");
            }}
            className="mt-8"
            ctaName="areas-quote"
            page="areas"
          >
            Request Local Support
          </CtaButton>
        </div>
      </section>
    </main>
  );
}

// =========================================================================
// ABOUT PAGE
// =========================================================================

function AboutPage({ setPage }: { setPage: (page: PageKey) => void }) {
  return (
    <main>
      {/* Hero */}
      <section className="border-b border-slate-200 bg-gradient-to-b from-slate-50 to-white">
        <div className="mx-auto max-w-7xl px-6 py-16 lg:py-20">
          <div className="max-w-3xl">
            <SectionLabel>About us</SectionLabel>
            <h1 className="mt-2 text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl">
              Your Tampa Bay technology partner
            </h1>
            <p className="mt-5 text-lg leading-relaxed text-slate-600">
              SightLine Networks was founded to give Tampa Bay businesses access
              to the same caliber of IT services, hosting, and technology
              infrastructure that large enterprises rely on — without the
              complexity or inflated costs.
            </p>
          </div>
        </div>
      </section>

      {/* Mission / Story */}
      <section className="mx-auto max-w-7xl px-6 py-20">
        <div className="grid gap-12 lg:grid-cols-2">
          <div>
            <h2 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
              Built for business. Based in Tampa Bay.
            </h2>
            <div className="mt-5 space-y-4 text-slate-600 leading-relaxed">
              <p>
                We started SightLine Networks because we saw too many local
                businesses struggling with unreliable IT vendors, confusing
                pricing, and technology that was holding them back instead of
                moving them forward.
              </p>
              <p>
                Today, we serve companies across Tampa, Clearwater, Largo, St.
                Petersburg, and beyond with managed IT, network installation,
                security cameras, web hosting, website development, and ongoing
                technology support.
              </p>
              <p>
                Our approach is simple: understand what your business actually
                needs, deliver it professionally, and be available when you need
                us. No jargon, no hidden fees, no excuses.
              </p>
            </div>
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            {[
              { value: "500+", label: "Projects completed", icon: CheckCircle2 },
              { value: "24/7", label: "System monitoring", icon: Clock3 },
              { value: "99.9%", label: "Hosting uptime", icon: Server },
              { value: "Same-day", label: "Urgent response", icon: Zap },
            ].map(({ value, label, icon: Icon }) => (
              <div
                key={label}
                className="flex flex-col items-center justify-center rounded-2xl border border-slate-200 bg-slate-50 p-6 text-center shadow-sm"
              >
                <Icon className="mb-3 h-6 w-6 text-sky-600" />
                <div className="text-2xl font-bold text-slate-900">{value}</div>
                <div className="mt-1 text-sm text-slate-500">{label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="border-y border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-7xl px-6 py-20">
          <div className="mb-12 max-w-2xl">
            <h2 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
              What guides how we work
            </h2>
          </div>

          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { title: "Clarity over complexity", text: "We explain technology in plain language, give clear proposals, and make sure you understand what you are paying for and why." },
              { title: "Proactive, not reactive", text: "We monitor systems, apply updates, and identify risks before they become problems. Prevention is always cheaper than recovery." },
              { title: "Local presence, real support", text: "We are based in the Tampa Bay area. When you need on-site help, we show up. When you call, a real person answers." },
              { title: "Right-sized solutions", text: "We recommend what your business actually needs — not the most expensive option. Solutions should match your size and goals." },
              { title: "Long-term relationships", text: "We build technology partnerships, not transactional vendor relationships. Our best clients have been with us for years." },
              { title: "Transparent pricing", text: "No hidden fees, no surprise invoices, no bait-and-switch. You will know exactly what services cost before we start." },
            ].map(({ title, text }) => (
              <div
                key={title}
                className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
              >
                <h3 className="text-base font-semibold text-slate-900">{title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-6 py-16">
        <div className="rounded-2xl bg-sky-600 px-8 py-12 text-center shadow-lg shadow-sky-200/30">
          <h2 className="text-2xl font-bold text-white sm:text-3xl">
            Let's talk about what your business needs
          </h2>
          <p className="mx-auto mt-3 max-w-lg text-sky-100">
            Whether you need managed IT, a new website, hosting, or a full
            technology overhaul — we're happy to discuss it.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <CtaButton
              variant="white"
              onClick={() => {
                trackCta("quote_click", "about_cta", "about");
                setPage("contact");
              }}
              ctaName="about-quote"
              page="about"
            >
              Get a Free Quote
            </CtaButton>
            <a
              href={PHONE_HREF}
              className="inline-flex items-center gap-2 rounded-xl border border-sky-500 px-5 py-3 text-sm font-medium text-white transition hover:bg-sky-700"
            >
              <Phone className="h-4 w-4" /> Call {PHONE}
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}

// =========================================================================
// CONTACT PAGE
// =========================================================================

function ContactPage({ setPage }: { setPage: (page: PageKey) => void }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    service: "",
    message: "",
  });
  const [status, setStatus] = useState<"idle" | "sending" | "success" | "error">("idle");

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus("sending");
    trackCta("form_submit", "contact_form", "contact");

    try {
      const res = await fetch(
        "https://jadtcuydqkdezwmvbdih.supabase.co/functions/v1/send-contact-email",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization:
              "Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImphZHRjdXlkcWtkZXp3bXZiZGloIiwicm9sZSI6ImFub24iLCJpYXQiOjE3Njc3MTEwMjksImV4cCI6MjA4MzI4NzAyOX0.dhMk9TkTaKCv1DGKKf0f4IO3ZYNbXI2UwG3zg10FA28",
          },
          body: JSON.stringify({
            name: form.name,
            company: form.company || "",
            email: form.email,
            phone: form.phone || "",
            subject: form.service || "General Inquiry",
            message: form.message || "No additional message provided",
            source: "main-website",
          }),
        }
      );

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || "Failed to send email");
      }

      setStatus("success");
      setForm({ name: "", email: "", phone: "", company: "", service: "", message: "" });
    } catch (err) {
      console.error("Contact form error:", err);
      setStatus("error");
    }
  };

  return (
    <main>
      {/* Hero */}
      <section className="border-b border-slate-200 bg-gradient-to-b from-slate-50 to-white">
        <div className="mx-auto max-w-7xl px-6 py-16 lg:py-20">
          <div className="max-w-3xl">
            <SectionLabel>Get in touch</SectionLabel>
            <h1 className="mt-2 text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl">
              Let's discuss your project
            </h1>
            <p className="mt-5 text-lg leading-relaxed text-slate-600">
              Tell us what your business needs and we will get back to you with a
              clear recommendation and a free, no-obligation quote — usually
              within one business day.
            </p>
          </div>
        </div>
      </section>

      {/* Form + sidebar */}
      <section className="mx-auto max-w-7xl px-6 py-20">
        <div className="grid gap-12 lg:grid-cols-[1.1fr_0.9fr]">
          {/* Form */}
          <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
            <h2 className="text-xl font-bold text-slate-900">
              Request a free quote
            </h2>
            <p className="mt-2 text-sm text-slate-500">
              Fill out the form and our team will respond within one business day.
            </p>

            <form
              onSubmit={handleSubmit}
              className="mt-6 space-y-5"
              id="contact-form"
              data-form="contact"
            >
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="name" className="mb-1.5 block text-sm font-medium text-slate-700">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={form.name}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/20"
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-slate-700">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={form.email}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/20"
                    placeholder="you@company.com"
                  />
                </div>
              </div>

              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="phone" className="mb-1.5 block text-sm font-medium text-slate-700">
                    Phone
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/20"
                    placeholder="(555) 000-0000"
                  />
                </div>
                <div>
                  <label htmlFor="company" className="mb-1.5 block text-sm font-medium text-slate-700">
                    Company
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={form.company}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/20"
                    placeholder="Your company"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="service" className="mb-1.5 block text-sm font-medium text-slate-700">
                  What do you need help with? *
                </label>
                <select
                  id="service"
                  name="service"
                  required
                  value={form.service}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm text-slate-900 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/20"
                >
                  <option value="">Select a service</option>
                  <option value="managed-it">Managed IT Support</option>
                  <option value="network">Network Installation</option>
                  <option value="cameras">Security Cameras</option>
                  <option value="server">Server & Cloud</option>
                  <option value="website">Website Development</option>
                  <option value="hosting">Web Hosting</option>
                  <option value="consulting">IT Consulting</option>
                  <option value="other">Other / Not Sure</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="mb-1.5 block text-sm font-medium text-slate-700">
                  Tell us about your project
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={4}
                  value={form.message}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/20"
                  placeholder="Describe your needs, timeline, or any questions you have..."
                />
              </div>

              <CtaButton
                type="submit"
                ctaName="contact-submit"
                page="contact"
                className="w-full sm:w-auto"
              >
                {status === "sending"
                  ? "Sending..."
                  : status === "success"
                  ? "Quote Requested!"
                  : "Submit Quote Request"}
              </CtaButton>

              {status === "success" && (
                <p className="text-sm font-medium text-emerald-600">
                  Thank you! We received your request and will be in touch within
                  one business day.
                </p>
              )}
              {status === "error" && (
                <p className="text-sm font-medium text-red-600">
                  Something went wrong. Please call us at {PHONE} or email{" "}
                  {EMAIL}.
                </p>
              )}
            </form>
          </div>

          {/* Sidebar */}
          <div className="flex flex-col gap-6">
            {/* Direct contact */}
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-slate-900">
                Prefer to talk directly?
              </h3>
              <div className="mt-4 space-y-4">
                <a
                  href={PHONE_HREF}
                  className="flex items-center gap-3 rounded-xl border border-slate-200 px-4 py-3 text-sm text-slate-700 transition hover:border-sky-300 hover:bg-sky-50"
                  onClick={() => trackCta("phone_click", "contact_sidebar", "contact")}
                >
                  <Phone className="h-5 w-5 text-sky-600" />
                  <div>
                    <div className="font-medium">{PHONE}</div>
                    <div className="text-xs text-slate-500">Call or text</div>
                  </div>
                </a>
                <a
                  href={`mailto:${EMAIL}`}
                  className="flex items-center gap-3 rounded-xl border border-slate-200 px-4 py-3 text-sm text-slate-700 transition hover:border-sky-300 hover:bg-sky-50"
                  onClick={() => trackCta("email_click", "contact_sidebar", "contact")}
                >
                  <Mail className="h-5 w-5 text-sky-600" />
                  <div>
                    <div className="font-medium">{EMAIL}</div>
                    <div className="text-xs text-slate-500">Email us anytime</div>
                  </div>
                </a>
                <div className="flex items-center gap-3 rounded-xl border border-slate-200 px-4 py-3 text-sm text-slate-700">
                  <MapPin className="h-5 w-5 text-sky-600" />
                  <div>
                    <div className="font-medium">Clearwater, FL</div>
                    <div className="text-xs text-slate-500">
                      Serving the greater Tampa Bay area
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Trust signals */}
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6">
              <h3 className="text-lg font-semibold text-slate-900">
                Why businesses choose SightLine
              </h3>
              <ul className="mt-4 space-y-3">
                {[
                  "Free, no-obligation quotes",
                  "Response within one business day",
                  "Local Tampa Bay team",
                  "Transparent pricing — no hidden fees",
                  "500+ projects completed",
                  "Certified engineers (Cisco, Microsoft, VMware)",
                ].map((item) => (
                  <li
                    key={item}
                    className="flex items-center gap-2 text-sm text-slate-600"
                  >
                    <CheckCircle2 className="h-4 w-4 shrink-0 text-sky-500" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Service links */}
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-slate-900">
                Explore our services
              </h3>
              <div className="mt-4 flex flex-col gap-2">
                {[
                  { label: "View all services", page: "services" as PageKey },
                  { label: "Hosting plans & pricing", page: "hosting" as PageKey },
                  { label: "Solutions by need", page: "solutions" as PageKey },
                  { label: "Areas we serve", page: "areas" as PageKey },
                ].map(({ label, page: p }) => (
                  <button
                    key={label}
                    onClick={() => setPage(p)}
                    className="flex items-center justify-between rounded-lg px-3 py-2 text-left text-sm text-slate-600 transition hover:bg-slate-50 hover:text-slate-900"
                  >
                    {label}
                    <ChevronRight className="h-4 w-4 text-slate-400" />
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

// =========================================================================
// MAIN APP COMPONENT
// =========================================================================

export default function SightlineWebsite() {
  const [page, setPage] = useState<PageKey>("home");
  const [mobileOpen, setMobileOpen] = useState(false);

  // Scroll to top on page change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" as ScrollBehavior });
    setMobileOpen(false);
  }, [page]);

  const CurrentPage = useMemo(() => {
    switch (page) {
      case "home":
        return <HomePage setPage={setPage} />;
      case "services":
        return <ServicesPage setPage={setPage} />;
      case "hosting":
        return <HostingPage setPage={setPage} />;
      case "solutions":
        return <SolutionsPage setPage={setPage} />;
      case "areas":
        return <AreasPage setPage={setPage} />;
      case "about":
        return <AboutPage setPage={setPage} />;
      case "contact":
        return <ContactPage setPage={setPage} />;
      case "managed-it":
        return <ManagedItPage setPage={setPage} />;
      case "network-installation":
        return <NetworkInstallationPage setPage={setPage} />;
      case "security-cameras":
        return <SecurityCamerasPage setPage={setPage} />;
      case "server-cloud":
        return <ServerCloudPage setPage={setPage} />;
      case "website-development":
        return <WebsiteDevelopmentPage setPage={setPage} />;
      case "it-consulting":
        return <ItConsultingPage setPage={setPage} />;
      default:
        return <HomePage setPage={setPage} />;
    }
  }, [page]);

  return (
    <div className="min-h-screen bg-white text-slate-900 antialiased">
      {/* SEO manager — updates document head on page change */}
      <SeoManager page={page} />

      {/* Skip to main content (accessibility) */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-[100] focus:rounded-lg focus:bg-sky-600 focus:px-4 focus:py-2 focus:text-white"
      >
        Skip to main content
      </a>

      <Header
        setPage={setPage}
        page={page}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
      />

      <div id="main-content">{CurrentPage}</div>

      <Footer setPage={setPage} />
    </div>
  );
}
